import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Lock } from 'lucide-react'

export default function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="nCinq Invoice" className="h-10 w-auto" />
                        </Link>
                        <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-600 font-medium transition-colors">
                            <ArrowLeft size={16} />
                            Back to Home
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Header */}
            <div className="pt-32 pb-12 bg-gray-50 border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl text-primary-600 mb-4 font-bold">
                        <Lock size={24} />
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                    <p className="text-gray-600">Last updated: January 12, 2026</p>
                </div>
            </div>

            {/* Content */}
            <div className="py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-primary max-w-none text-gray-700 space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                            <p>
                                nCinq ("we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you use nCinq Invoice ("the Service").
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                            <div className="space-y-4">
                                <p>We collect information that you provide directly to us, including:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Account Information:</strong> Name, email address, password, and business details.</li>
                                    <li><strong>Client Information:</strong> Names, email addresses, and contact details of your clients.</li>
                                    <li><strong>Financial Information:</strong> Payment details, transaction history, and invoice records.</li>
                                    <li><strong>Support Communications:</strong> Information you provide when contacting our support team.</li>
                                </ul>
                                <p>We also automatically collect certain technical information, such as your IP address, browser type, and usage patterns.</p>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                            <p>We use the collected data for various purposes:</p>
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                                <li>To provide, maintain, and improve our Service.</li>
                                <li>To process transactions and send related information (invoices, receipts).</li>
                                <li>To send administrative messages, updates, and security alerts.</li>
                                <li>To respond to your comments, questions, and support requests.</li>
                                <li>To monitor and analyze trends, usage, and activities.</li>
                                <li>To detect, prevent, and address technical issues or fraudulent activity.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
                            <p>
                                We do not sell your personal data. We may share information with third-party service providers who perform services on our behalf (e.g., payment processors, hosting providers) only as necessary for them to provide those services.
                            </p>
                            <p className="mt-4">
                                We may also disclose information if required by law or in response to valid requests by public authorities (e.g., a court or government agency).
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                            <p>
                                We implement bank-level security measures to protect your data, including 256-bit SSL encryption for data transmission and secure cloud storage. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Data Rights</h2>
                            <p>
                                Depending on your location, you may have the following rights regarding your personal data:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 mt-4">
                                <li>The right to access the data we hold about you.</li>
                                <li>The right to request correction of inaccurate data.</li>
                                <li>The right to request deletion of your data.</li>
                                <li>The right to object to or restrict processing of your data.</li>
                                <li>The right to data portability.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies</h2>
                            <p>
                                We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to This Policy</h2>
                            <p>
                                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
                            <p>
                                If you have any questions about this Privacy Policy, please contact us:
                            </p>
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <p className="font-semibold text-gray-900">nCinq Privacy Office</p>
                                <p>Email: privacy@ncinq.app</p>
                                <p>Address: Georgetown, Guyana</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-50 border-t border-gray-100 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
                    <p>© 2026 nCinq Invoice. All rights reserved.</p>
                </div>
            </footer>
        </div>
    )
}
